/**
 * Sprint 11.1 — Role-scoped Dashboard wired to /api/v1/dashboard/overview.
 *
 * Replaces the legacy STORE-backed prototype overview. The KPI tiles, funnel
 * widget, and activity feed are now all real DB-derived data, automatically
 * scoped to the caller (admin → system-wide, hr_recruiter → their assigned
 * candidates, hiring_manager → their role+department).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { v1Get } from '@/lib/api';
import { parseServerDate, fmtDateIST } from '@/lib/format';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users, Clock, Sparkles, CalendarClock, UserPlus, Video, Mail,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Gated, useMyPermissions, permissionPayloadAllows } from '@/auth/permissions';
import { ActivityFeedRow } from '@/components/ActivityFeedRow';

const KPI_ICONS = {
  active_candidates: Users,
  awaiting_review: Clock,
  new_last_7d: UserPlus,
  panel_evaluations_30d: Sparkles,
  emails_sent_30d: Mail,
};

const ROLE_TITLES = {
  admin: 'Admin Dashboard',
  hr_manager: 'HR Manager Dashboard',
  hr_recruiter: 'HR Dashboard',
  hiring_manager: 'Hiring Manager Dashboard',
  interviewer: 'Interviewer Dashboard',
};

const ROLE_SUBTITLES = {
  admin: 'System-wide pipeline health, recent activity, and operations signals.',
  hr_manager: 'Pipeline health across all roles and departments you oversee.',
  hr_recruiter: 'Your assigned candidates, pending reviews, and recent activity.',
  hiring_manager: 'Candidates in your role and department scope.',
  interviewer: 'Interviews and reviews assigned to you.',
};

const fmtTs = (iso) => {
  if (!iso) return '';
  try {
    const d = parseServerDate(iso);
    if (!d) return iso;
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return fmtDateIST(d, { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
};

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['v1-dashboard-overview'],
    queryFn: () => v1Get('/dashboard/overview'),
    refetchInterval: 60_000,
  });
  const { data: myPerms } = useMyPermissions();
  const canViewCandidates = permissionPayloadAllows(myPerms, {
    anyOf: ['candidate.view_all', 'candidate.view_assigned'],
  });

  const role = data?.role || 'hr_recruiter';
  const title = ROLE_TITLES[role] || 'Dashboard';
  const subtitle = ROLE_SUBTITLES[role] || '';

  const kpis = data?.kpis || [];
  const funnel = data?.funnel || [];
  const activity = data?.activity || [];

  return (
    <div className="space-y-6">
      <PageHeader title={title} subtitle={subtitle} />

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-kpi-grid">
        {isLoading && (
          <>
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="card-elevated"><CardContent className="p-5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="mt-3 h-8 w-16" />
                <Skeleton className="mt-2 h-2 w-32" />
              </CardContent></Card>
            ))}
          </>
        )}
        {!isLoading && kpis.map((k) => (
          <KpiCard
            key={k.kind}
            label={k.label}
            value={k.value}
            caption={k.caption}
            icon={KPI_ICONS[k.kind] || Users}
            to={
              k.kind === 'active_candidates' && canViewCandidates
                ? '/portal/candidates?eval_status=has_active'
                : undefined
            }
            data-testid={`kpi-${k.kind}`}
          />
        ))}
      </div>

      {/* Funnel + Activity */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2 card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-semibold">Pipeline funnel</CardTitle>
            <span className="text-xs text-muted-foreground">Active candidates by stage</span>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : funnel.every((f) => f.count === 0) ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No active candidates in your scope yet.
              </p>
            ) : (
              <ul className="space-y-3" data-testid="dashboard-funnel">
                {funnel.map((f) => {
                  const max = Math.max(1, ...funnel.map((x) => x.count));
                  const pct = Math.round((f.count / max) * 100);
                  return (
                    <li key={f.stage_code}>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{f.stage}</span>
                        <span className="tabular-nums">{f.count}</span>
                      </div>
                      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${pct}%`, transition: 'width 200ms ease' }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-semibold">Recent activity</CardTitle>
            <Link to="/portal/activity" className="text-xs font-medium text-primary hover:underline">
              View all
            </Link>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-80 pr-3">
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : activity.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No recent activity yet.
                </p>
              ) : (
                <ul className="space-y-2.5" data-testid="dashboard-activity-feed">
                  {activity.map((a) => (
                    <li key={a.id}>
                      <ActivityFeedRow item={a} fmtTs={fmtTs} testId={`dashboard-activity-${a.id}`} />
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Quick actions — hide non-applicable ones for Hiring Manager */}
      {role !== 'hiring_manager' && (
        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-sm font-semibold">Quick actions</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Common tasks for your role.</p>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
              <Gated need="candidate.create">
                <Link to="/portal/candidates/new">
                  <Button variant="outline" className="w-full justify-start gap-2" data-testid="quick-action-new-candidate">
                    <UserPlus className="h-4 w-4" /> New Candidate
                  </Button>
                </Link>
              </Gated>
              <Gated anyOf={['candidate.view_all', 'candidate.view_assigned']}>
                <Link to="/portal/candidates">
                  <Button variant="outline" className="w-full justify-start gap-2" data-testid="quick-action-browse">
                    <Users className="h-4 w-4" /> Browse Candidates
                  </Button>
                </Link>
              </Gated>
              <Gated need="pipeline.edit">
                <Link to="/portal/settings/pipelines">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <CalendarClock className="h-4 w-4" /> Configure Pipelines
                  </Button>
                </Link>
              </Gated>
              <Gated need="bank.videobot.view">
                <Link to="/portal/settings/questions/videobot">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Video className="h-4 w-4" /> Manage Video Bot Bank
                  </Button>
                </Link>
              </Gated>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
